import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Visually builds a CSS border-radius effect, with the option to link all four corners together or adjust each independently, and generates the corresponding CSS code.`,
  how: `Adjust each corner's radius using the sliders, with a live preview showing the result, then copy the generated CSS. Turn on Elliptical corners to give each corner a separate horizontal and vertical radius (CSS writes these with a slash), or press Blob shape for an organic starting point. Switching to % lowers any radius above 50% to 50% and tells you which.`,
  tip: `Uneven, independently-set corner radii can create a distinctive "blob" or organic shape effect that's become a popular design trend — worth experimenting with unlinked corners rather than just a uniform rounded rectangle.`,
};

/** Shortest valid border-radius shorthand for TL TR BR BL. */
export function radiusShorthand(tl: number, tr: number, br: number, bl: number, unit: "px" | "%" = "px"): string {
  const u = (n: number) => `${n}${unit}`;
  if (tl === tr && tr === br && br === bl) return u(tl);
  if (tl === br && tr === bl) return `${u(tl)} ${u(tr)}`;
  if (tr === bl) return `${u(tl)} ${u(tr)} ${u(br)}`;
  return `${u(tl)} ${u(tr)} ${u(br)} ${u(bl)}`;
}

/** One side of the shorthand for TL TR BR BL, as short as CSS allows (1, 2, 3 or 4 values). */
function sideValues(tl: number, tr: number, br: number, bl: number, unit: "px" | "%"): string {
  return radiusShorthand(tl, tr, br, bl, unit);
}

/**
 * Elliptical shorthand: horizontal radii, a slash, vertical radii, each written as short as CSS allows.
 * When every corner is circular (horizontal equals vertical) the slash is dropped, so the plain form is unchanged.
 */
export function ellipticalShorthand(h: [number, number, number, number], v: [number, number, number, number], unit: "px" | "%" = "px"): string {
  const hs = sideValues(h[0], h[1], h[2], h[3], unit);
  if (h.every((n, i) => n === v[i])) return hs;
  return `${hs} / ${sideValues(v[0], v[1], v[2], v[3], unit)}`;
}

/** Corners that a switch to % would lower to the 50% maximum, by name. */
export function cornersOverPercentCap(values: { name: string; v: number }[]): string[] {
  return values.filter(c => c.v > 50).map(c => c.name);
}

export default function BorderRadiusGenerator() {
  const [linked, setLinked] = useState(true);
  const [tl, setTl] = useState(20);
  const [tr, setTr] = useState(20);
  const [br, setBr] = useState(20);
  const [bl, setBl] = useState(20);
  // Vertical radii, used only in elliptical mode (the horizontal ones above are then the horizontal radius).
  const [elliptical, setElliptical] = useState(false);
  const [vtl, setVtl] = useState(20);
  const [vtr, setVtr] = useState(20);
  const [vbr, setVbr] = useState(20);
  const [vbl, setVbl] = useState(20);
  const [capNote, setCapNote] = useState<string | null>(null);

  const setAll = (v: number) => {
    setTl(v);
    setTr(v);
    setBr(v);
    setBl(v);
  };
  const setAllV = (v: number) => {
    setVtl(v);
    setVtr(v);
    setVbr(v);
    setVbl(v);
  };

  const [unit, setUnit] = useState<"px" | "%">("px");
  const value = elliptical
    ? ellipticalShorthand([tl, tr, br, bl], [vtl, vtr, vbr, vbl], unit)
    : radiusShorthand(tl, tr, br, bl, unit);
  const css = `border-radius: ${value};`;

  const changeUnit = (u: "px" | "%") => {
    if (u === unit) return;
    setUnit(u);
    if (u === "%") {
      const all = [
        { name: "Top Left", v: tl }, { name: "Top Right", v: tr }, { name: "Bottom Left", v: bl }, { name: "Bottom Right", v: br },
        ...(elliptical ? [{ name: "Top Left (vertical)", v: vtl }, { name: "Top Right (vertical)", v: vtr }, { name: "Bottom Left (vertical)", v: vbl }, { name: "Bottom Right (vertical)", v: vbr }] : []),
      ];
      const capped = cornersOverPercentCap(all);
      setCapNote(capped.length ? `Percent radii go up to 50%, so ${capped.join(", ")} ${capped.length === 1 ? "was" : "were"} lowered to 50%.` : null);
      setTl(Math.min(tl, 50)); setTr(Math.min(tr, 50)); setBr(Math.min(br, 50)); setBl(Math.min(bl, 50));
      setVtl(Math.min(vtl, 50)); setVtr(Math.min(vtr, 50)); setVbr(Math.min(vbr, 50)); setVbl(Math.min(vbl, 50));
    } else {
      setCapNote(null);
    }
  };

  /** An organic starting point: elliptical, unlinked, in %. */
  const blob = () => {
    setUnit("%"); setElliptical(true); setLinked(false); setCapNote(null);
    setTl(40); setTr(50); setBr(40); setBl(50);
    setVtl(50); setVtr(40); setVbr(50); setVbl(40);
  };

  const copy = () => {
    void copyText(css, "Copied to clipboard");
  };

  // Called as a function (not <Corner/>) so it isn't remounted - and the slider dropped mid-drag - on every change.
  const Corner = ({
    label,
    value,
    onChange,
    onAll = setAll,
    suffix = "",
  }: {
    label: string;
    value: number;
    onChange: (v: number) => void;
    onAll?: (v: number) => void;
    suffix?: string;
  }) => (
    <div>
      <div className="flex justify-between mb-1">
        <Label className="text-sm" htmlFor={`br-${label}${suffix}`}>{label}{suffix}</Label>
        <span className="text-sm text-muted-foreground">{value}{unit}</span>
      </div>
      <input
        id={`br-${label}${suffix}`}
        aria-label={`${label}${suffix} radius`}
        type="range"
        min={0}
        max={unit === "%" ? 50 : 200}
        value={value}
        onChange={e => {
          setCapNote(null);
          return linked
            ? onAll(Number(e.target.value))
            : onChange(Number(e.target.value));
        }}
        className="w-full"
      />
    </div>
  );

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Switch id="br-link" checked={linked} onCheckedChange={setLinked} aria-label="Link all corners" />
        <Label className="text-sm" htmlFor="br-link">Link all corners</Label>
        <Switch id="br-ellip" className="ml-4" checked={elliptical} onCheckedChange={setElliptical} aria-label="Elliptical corners" />
        <Label className="text-sm" htmlFor="br-ellip">Elliptical corners</Label>
        <Button size="sm" variant="outline" onClick={blob}>Blob shape</Button>
        <div className="ml-auto flex gap-1" role="group" aria-label="Unit">
          {(["px", "%"] as const).map(u => (
            <Button key={u} size="sm" variant={unit === u ? "default" : "outline"} aria-pressed={unit === u} onClick={() => changeUnit(u)}>{u}</Button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="grid grid-cols-2 gap-4">
          {Corner({ label: "Top Left", suffix: elliptical ? " (horizontal)" : "", value: tl, onChange: setTl })}
          {Corner({ label: "Top Right", suffix: elliptical ? " (horizontal)" : "", value: tr, onChange: setTr })}
          {Corner({ label: "Bottom Left", suffix: elliptical ? " (horizontal)" : "", value: bl, onChange: setBl })}
          {Corner({ label: "Bottom Right", suffix: elliptical ? " (horizontal)" : "", value: br, onChange: setBr })}
          {elliptical && (<>
            {Corner({ label: "Top Left", suffix: " (vertical)", value: vtl, onChange: setVtl, onAll: setAllV })}
            {Corner({ label: "Top Right", suffix: " (vertical)", value: vtr, onChange: setVtr, onAll: setAllV })}
            {Corner({ label: "Bottom Left", suffix: " (vertical)", value: vbl, onChange: setVbl, onAll: setAllV })}
            {Corner({ label: "Bottom Right", suffix: " (vertical)", value: vbr, onChange: setVbr, onAll: setAllV })}
          </>)}
        </div>
        <div className="flex items-center justify-center">
          <div
            className="w-48 h-48 bg-primary/20 border-2 border-primary"
            style={{ borderRadius: value }}
          />
        </div>
      </div>
      {capNote && <p role="status" className="mt-4 text-sm text-muted-foreground">{capNote}</p>}
      <div className="mt-6">
        <div className="rounded-lg border bg-muted/30 p-4 font-mono text-sm">
          {blocked ? "— blocked at today's usage limit —" : css}
        </div>
        <Button className="mt-3" onClick={copy} disabled={blocked}>
          Copy CSS
        </Button>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
